import { json } from './json'
import { crypt } from './crypt'
import { CURRENT_VERSION, VERSION_KEY } from './version'

interface StorageData<T = any> {
  value: T
  timestamp: number
  version?: string
}

interface Storage {
  get<T>(key: string, nestedKeyPath?: string, passphrase?: string): T | null
  set<T>(key: string, value: T, nestedKeyPath?: string, passphrase?: string): void
  remove(key: string): void
}

function shouldRemove(value: any): boolean {
  return value === undefined || value === null
}

function safeEncrypt(passphrase: string | undefined, serialized: string): string {
  if (!passphrase) return serialized
  try {
    return crypt.encrypt(passphrase, serialized)
  } catch {
    return serialized
  }
}

function safeLocalStorageSet(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value)
    return true
  } catch {
    try {
      localStorage.removeItem(key)
    } catch {}
    return false
  }
}

function getData(key: string, passphrase?: string): any {
  const storedData = localStorage.getItem(key)
  if (!storedData) return null
  const payload =
    passphrase && crypt.isHexString(storedData)
      ? (() => {
          try {
            return crypt.decrypt(passphrase, storedData)
          } catch {
            return storedData
          }
        })()
      : storedData
  const parsed = json.parse(payload) ?? json.parse(storedData)
  if (parsed === null) {
    const trimmed = storedData.trim()
    const looksLikeJSON = trimmed.startsWith('{') || trimmed.startsWith('[') || trimmed.startsWith('"')
    if (looksLikeJSON) {
      try {
        localStorage.removeItem(key)
      } catch {}
    }
    return null
  }
  return parsed
}

function saveData(key: string, data: any, passphrase?: string): void {
  if (shouldRemove(data)) {
    try {
      localStorage.removeItem(key)
    } catch {}
    return
  }
  const serialized = json.stringify(data)
  if (serialized === null) {
    try {
      localStorage.removeItem(key)
    } catch {}
    return
  }
  const toStore = safeEncrypt(passphrase, serialized)
  safeLocalStorageSet(key, toStore)
}

function getStorageData<T>(key: string, passphrase?: string): StorageData<T> | null {
  const data = getData(key, passphrase)
  if (!data) return null

  if (!data.value && !data.timestamp) {
    return {
      value: data,
      timestamp: Date.now(),
      version: CURRENT_VERSION
    }
  }

  return data
}

function saveStorageData<T>(key: string, value: T, passphrase?: string): void {
  if (shouldRemove(value)) {
    try {
      localStorage.removeItem(key)
    } catch {}
    return
  }
  const storageData: StorageData<T> = {
    value,
    timestamp: Date.now(),
    version: CURRENT_VERSION
  }
  saveData(key, storageData, passphrase)
}

function setOrUpdateNestedValueAtPath(data: any, nestedKeyPath: string, value: any): void {
  const keys = nestedKeyPath.split('.')
  let obj = data
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i]
    if (!key) continue
    if (obj[key] == null || (typeof obj[key] !== 'object' && typeof obj[key] !== 'function')) {
      obj[key] = {}
    }
    obj = obj[key]
  }
  const lastKey = keys[keys.length - 1]
  if (lastKey) {
    obj[lastKey] = value
  }
}

function getNestedValueAtPath(data: any, nestedKeyPath: string): any {
  const keys = nestedKeyPath.split('.')
  let obj = data
  for (const key of keys) {
    if (!key) return null
    if (obj == null || (typeof obj !== 'object' && typeof obj !== 'function')) return null
    if (!Object.prototype.hasOwnProperty.call(obj, key)) return null
    obj = obj[key]
  }
  return obj
}

export const storage: Storage = {
  get<T>(key: string, nestedKeyPath?: string, passphrase?: string): T | null {
    const storageData = getStorageData<T>(key, passphrase)

    if (!storageData) return null

    if (!nestedKeyPath) {
      return storageData.value || null
    }

    return storageData.value ? getNestedValueAtPath(storageData.value, nestedKeyPath) : null
  },

  set<T>(key: string, value: T, nestedKeyPath?: string, passphrase?: string): void {
    let storageData = getStorageData<T>(key, passphrase)
    let currentValue: T

    if (!storageData) {
      currentValue = nestedKeyPath ? ({} as T) : (undefined as unknown as T)
    } else {
      currentValue = storageData.value
    }

    if (!nestedKeyPath) {
      currentValue = value
      if (currentValue === undefined || currentValue === null) {
        this.remove(key)
        return
      }
    } else {
      const base: any = currentValue == null || typeof currentValue !== 'object' ? {} : currentValue
      setOrUpdateNestedValueAtPath(base, nestedKeyPath, value)
      currentValue = base as T
    }

    if (Array.isArray(currentValue)) {
      currentValue = currentValue.filter((entry) => entry !== null && entry !== undefined) as T
    }

    saveStorageData(key, currentValue, passphrase)
  },

  remove(key: string): void {
    localStorage.removeItem(key)
  }
}
